/**
 * VixoKart Theme — Collection Filters (Phase 5 Rebuild)
 * Handles: desktop filter sidebar, mobile filter drawer with Apply,
 * AJAX filtering, active chips, dual-thumb price slider, sort-by,
 * URL state management, grid/list view toggle, Load More.
 *
 * Preserves all existing theme functionality. Extends mobile support.
 */

'use strict';

// The collection section loads this asset with `defer`. Keep collection
// interactions alive even if this section script runs before theme.js.
window.VixoKart = window.VixoKart || {};
VixoKart.debounce = VixoKart.debounce || function(fn, delay = 300) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

(function initCollectionFilters() {
  // ─── Element refs ────────────────────────────────────────────────────────────

  const desktopForm   = document.querySelector('[data-filter-form]');
  const mobileForm    = document.querySelector('[data-filter-form-mobile]');
  const gridWrap      = document.querySelector('[data-product-grid]');
  const countEl       = document.querySelector('[data-products-count]');
  const activeChips   = document.querySelector('[data-active-filters]');

  // Guard: exit if no filter form present (non-collection pages)
  if (!desktopForm && !mobileForm) return;

  // ─── Resolve section ID for AJAX section rendering ───────────────────────────

  function getSectionId() {
    const collectionRoot =
      document.querySelector('.main-collection[data-section-id]') ||
      document.querySelector('[data-section-id].section-main-collection');
    return collectionRoot ? collectionRoot.dataset.sectionId : '';
  }

  // ─── AJAX filter / grid update ───────────────────────────────────────────────

  async function applyFilters(formEl) {
    if (!formEl) return;

    const params    = new URLSearchParams(new FormData(formEl)).toString();
    const sectionId = getSectionId();
    const fetchUrl  = `${window.location.pathname}?${params}${sectionId ? '&section_id=' + sectionId : ''}`;

    gridWrap?.classList.add('loading');

    try {
      const response = await fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const text     = await response.text();
      const doc      = new DOMParser().parseFromString(text, 'text/html');

      // Replace product grid content
      const newGrid = doc.querySelector('[data-product-grid]');
      if (newGrid && gridWrap) gridWrap.innerHTML = newGrid.innerHTML;

      // Update product count
      const newCount = doc.querySelector('[data-products-count]');
      if (newCount && countEl) countEl.textContent = newCount.textContent;

      // Update active filter chips
      const newChips = doc.querySelector('[data-active-filters]');
      if (newChips && activeChips) activeChips.innerHTML = newChips.innerHTML;

      // Update Load More button if present
      const newLoadMore   = doc.querySelector('[data-load-more]');
      const curLoadMore   = document.querySelector('[data-load-more]');
      const loadMoreWrap  = curLoadMore?.closest('.load-more-wrap');
      if (newLoadMore && loadMoreWrap) {
        loadMoreWrap.innerHTML = loadMoreWrap.innerHTML; // keep wrap
        const btn = loadMoreWrap.querySelector('[data-load-more]');
        if (btn) btn.dataset.nextUrl = newLoadMore.dataset.nextUrl;
      } else if (!newLoadMore && curLoadMore) {
        curLoadMore.closest('.load-more-wrap')?.remove();
      }

      // Push URL state
      window.history.pushState({}, '', `${window.location.pathname}?${params}`);

      // Re-observe scroll reveal on new cards
      document.querySelectorAll('[data-product-grid] .reveal:not(.visible)').forEach(el => {
        VixoKart.observeReveal?.(el);
      });

    } catch (err) {
      console.error('[VixoKart] Filter error:', err);
    } finally {
      gridWrap?.classList.remove('loading');
    }
  }

  // Debounced desktop form filter apply (400 ms)
  const debouncedDesktopFilter = VixoKart.debounce(() => applyFilters(desktopForm), 400);

  // Desktop form — apply on any checkbox / price change
  desktopForm?.addEventListener('change', debouncedDesktopFilter);

  // ─── Sort-by (all sort selects: desktop toolbar + mobile bar) ────────────────

  document.querySelectorAll('[data-sort-by]').forEach(select => {
    select.addEventListener('change', (e) => {
      const url = new URL(window.location);
      url.searchParams.set('sort_by', e.target.value);
      window.location = url.toString();
    });
  });

  // ─── Mobile filter drawer ─────────────────────────────────────────────────────

  const openFilterBtns  = document.querySelectorAll('[data-open-filters]');
  const closeFilterBtn  = document.querySelector('[data-close-filters]');
  const filterDrawer    = document.querySelector('.filter-drawer');
  const filterOverlay   = document.querySelector('[data-filter-overlay]');
  const applyMobileBtn  = document.querySelector('[data-apply-mobile-filters]');

  function openFilters() {
    if (!filterDrawer) return;
    filterDrawer.classList.add('active');
    filterDrawer.setAttribute('aria-hidden', 'false');
    filterOverlay?.classList.add('active');
    filterOverlay?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';

    openFilterBtns.forEach(btn => btn.setAttribute('aria-expanded', 'true'));

    // Focus first interactive element in drawer
    const firstFocusable = filterDrawer.querySelector('button, input, select, a[href]');
    firstFocusable?.focus();
  }

  function closeFilters() {
    if (!filterDrawer) return;
    filterDrawer.classList.remove('active');
    filterDrawer.setAttribute('aria-hidden', 'true');
    filterOverlay?.classList.remove('active');
    filterOverlay?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    openFilterBtns.forEach(btn => btn.setAttribute('aria-expanded', 'false'));

    // Return focus to the open button
    openFilterBtns[0]?.focus();
  }

  openFilterBtns.forEach(btn => btn.addEventListener('click', openFilters));
  closeFilterBtn?.addEventListener('click', closeFilters);
  filterOverlay?.addEventListener('click', closeFilters);

  // Apply mobile filters — submit mobile form via AJAX then close
  applyMobileBtn?.addEventListener('click', () => {
    if (mobileForm) {
      applyFilters(mobileForm);
      closeFilters();
    } else if (desktopForm) {
      applyFilters(desktopForm);
      closeFilters();
    }
  });

  // Trap focus inside open drawer (Tab key)
  filterDrawer?.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      filterDrawer.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')
    ).filter(el => !el.closest('[aria-hidden="true"]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // ESC key closes drawer
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && filterDrawer?.classList.contains('active')) {
      closeFilters();
    }
  });

  // ─── Filter group accordions ──────────────────────────────────────────────────

  // Works for both desktop sidebar and mobile drawer groups
  document.querySelectorAll('.filter-group__title').forEach(title => {
    // Initialise height for smooth animation
    const group    = title.closest('.filter-group');
    const content  = group?.querySelector('.filter-group__options');
    if (content) {
      content.style.height = content.scrollHeight + 'px';
    }

    title.addEventListener('click', () => {
      const grp     = title.closest('.filter-group');
      const panel   = grp?.querySelector('.filter-group__options');
      const isOpen  = title.getAttribute('aria-expanded') === 'true';

      title.setAttribute('aria-expanded', String(!isOpen));
      grp?.classList.toggle('filter-group--collapsed', isOpen);

      if (panel) {
        if (isOpen) {
          // Collapse
          panel.style.height = panel.scrollHeight + 'px';
          requestAnimationFrame(() => { panel.style.height = '0'; });
        } else {
          // Expand
          panel.style.height = panel.scrollHeight + 'px';
          panel.addEventListener('transitionend', () => {
            if (title.getAttribute('aria-expanded') === 'true') {
              panel.style.height = 'auto';
            }
          }, { once: true });
        }
      }
    });
  });

  // ─── Clear all filters ────────────────────────────────────────────────────────

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-clear-filters]')) {
      e.preventDefault();
      window.location = window.location.pathname;
    }
  });

  // ─── Price slider — dual-thumb, works for both desktop and mobile ─────────────

  function initPriceSlider(container) {
    if (!container) return;

    // Desktop uses explicit IDs; mobile uses data attributes
    const rangeMin  = container.querySelector('[id^="range-min"], [data-mobile-range="min"]');
    const rangeMax  = container.querySelector('[id^="range-max"], [data-mobile-range="max"]');
    const inputMin  = container.querySelector('[name="filter.v.price.gte"]');
    const inputMax  = container.querySelector('[name="filter.v.price.lte"]');
    const fill      = container.querySelector('.price-range-slider__fill');

    if (!rangeMin || !rangeMax) return;

    function sync() {
      const min  = parseFloat(rangeMin.value || 0);
      const max  = parseFloat(rangeMax.value  || 0);
      const ceil = parseFloat(rangeMax.max    || 100);

      if (inputMin) inputMin.value = min;
      if (inputMax) inputMax.value = max;

      const minPct = (min / ceil) * 100;
      const maxPct = (max / ceil) * 100;

      // The visual bar is entirely driven by .price-range-slider__fill's
      // left/width below — the range inputs themselves are transparent
      // (see .range-slider in theme.css), so no CSS custom properties
      // are needed here anymore.
      if (fill) {
        fill.style.left  = minPct + '%';
        fill.style.width = Math.max(0, maxPct - minPct) + '%';
      }
    }

    rangeMin.addEventListener('input', () => {
      if (parseFloat(rangeMin.value) > parseFloat(rangeMax.value)) {
        rangeMin.value = rangeMax.value;
      }
      sync();
    });

    rangeMax.addEventListener('input', () => {
      if (parseFloat(rangeMax.value) < parseFloat(rangeMin.value)) {
        rangeMax.value = rangeMin.value;
      }
      sync();
    });

    inputMin?.addEventListener('change', () => {
      if (rangeMin) rangeMin.value = inputMin.value;
      sync();
    });

    inputMax?.addEventListener('change', () => {
      if (rangeMax) rangeMax.value = inputMax.value;
      sync();
    });

    sync();
  }

  // Init price sliders for both desktop and mobile
  document.querySelectorAll('.price-range-slider').forEach(slider => initPriceSlider(slider));

  // ─── Browser back / forward support ──────────────────────────────────────────

  window.addEventListener('popstate', () => window.location.reload());

  // ─── Load More ────────────────────────────────────────────────────────────────

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-load-more]');
    if (!btn) return;

    const nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;

    btn.classList.add('btn--loading');
    btn.disabled = true;

    try {
      const response = await fetch(nextUrl);
      const text     = await response.text();
      const doc      = new DOMParser().parseFromString(text, 'text/html');

      // Append new product cards to the existing grid
      const newItems = doc.querySelectorAll('[data-product-grid] > *');
      const grid     = document.querySelector('[data-product-grid]');
      newItems.forEach(item => {
        grid?.appendChild(item);
        VixoKart.observeReveal?.(item);
      });

      // Update product count if available
      const newCount = doc.querySelector('[data-products-count]');
      if (newCount && countEl) countEl.textContent = newCount.textContent;

      // Advance next URL or remove button when no more pages
      const newNextBtn = doc.querySelector('[data-load-more]');
      if (newNextBtn?.dataset.nextUrl) {
        btn.dataset.nextUrl = newNextBtn.dataset.nextUrl;
        btn.disabled = false;
        btn.classList.remove('btn--loading');
      } else {
        btn.closest('.load-more-wrap')?.remove();
      }

    } catch (err) {
      console.error('[VixoKart] Load more error:', err);
      btn.classList.remove('btn--loading');
      btn.disabled = false;
    }
  });

  // ─── Grid / List view toggle ──────────────────────────────────────────────────

  const viewToggle = document.querySelector('.view-toggle');
  const productGrid = document.querySelector('.product-grid');

  if (viewToggle && productGrid) {
    viewToggle.querySelectorAll('.view-toggle__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        viewToggle.querySelectorAll('.view-toggle__btn').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        productGrid.dataset.view = btn.dataset.view;
      });
    });
  }

})();
